import { ref, computed, onMounted, onUnmounted } from 'vue';

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

/**
 * AURA Responsive Composable
 * Detects current viewport breakpoint and provides responsive helpers
 */
export const useAuraResponsive = () => {
    const getWindowWidth = () => (typeof window === 'undefined' ? 1024 : window.innerWidth);
    const getWindowHeight = () => (typeof window === 'undefined' ? 768 : window.innerHeight);
    const windowWidth = ref(getWindowWidth());
    const windowHeight = ref(getWindowHeight());

    const breakpoints = {
        mobile: 768, // md breakpoint
        tablet: 1024, // lg breakpoint
    };

    const isCompactLandscape = computed(
        () => windowHeight.value <= 500 && windowWidth.value > windowHeight.value,
    );

    const breakpoint = computed<Breakpoint>(() => {
        if (isCompactLandscape.value) return 'mobile';
        if (windowWidth.value < breakpoints.mobile) return 'mobile';
        if (windowWidth.value < breakpoints.tablet) return 'tablet';
        return 'desktop';
    });

    const isMobile = computed(() => breakpoint.value === 'mobile');
    const isTablet = computed(() => breakpoint.value === 'tablet');
    const isDesktop = computed(() => breakpoint.value === 'desktop');

    const updateWidth = () => {
        windowWidth.value = getWindowWidth();
        windowHeight.value = getWindowHeight();
    };

    onMounted(() => {
        window.addEventListener('resize', updateWidth);
        updateWidth();
    });

    onUnmounted(() => {
        window.removeEventListener('resize', updateWidth);
    });

    return {
        breakpoint,
        isMobile,
        isTablet,
        isDesktop,
        isCompactLandscape,
        windowWidth: computed(() => windowWidth.value),
    };
};
